import LoadingElement from '@/components/shared/loadingElement';
import tw from 'twin.macro';
import plus from '@assets/svgs/common/plus.svg';

/**
 * PrimaryButton component – primary action button with optional loading state and float (FAB) mode.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.text] - Button label; when isFloatMode is true on mobile, a plus icon is shown instead.
 * @param {Function} [props.handleClick] - Callback fired when the button is clicked.
 * @param {boolean} [props.isSubmitting] - When true, shows a loading spinner (and text when provided) instead of normal content.
 * @param {boolean} [props.disable] - When true, the button is disabled and uses muted styling.
 * @param {string} [props.type='submit'] - The button type attribute (e.g. 'submit', 'button').
 * @param {Object|import('twin.macro').TwStyle} [props.customStyle] - Additional styles for the button element.
 * @param {boolean} [props.isFloatMode=false] - When true, on mobile renders as a floating action button (bottom-right, circular with plus icon).
 * @param {React.RefObject} [props.ref] - Ref attached to the button element.
 * @param {Object|import('twin.macro').TwStyle} [props.textElementStyle] - Additional styles for the inner text/icon wrapper.
 * Any other props are spread onto the button element.
 * @returns {React.ReactElement}
 */
const PrimaryButton = ({
  text,
  handleClick,
  isSubmitting,
  disable,
  type = 'submit',
  customStyle,
  isFloatMode = false,
  ref,
  textElementStyle,
  ...rest
}) => {
  return (
    <button
      onClick={handleClick}
      type={type}
      ref={ref}
      css={[
        tw`border text-text_primary text-[0.85rem] p-2.5 transition-all duration-500 rounded-[10px] [line-height: 133%] [letter-spacing: 2%] font-bold`,
        tw`max-md:(text-[0.9375rem] font-medium [line-height: normal] rounded-md)`,
        disable
          ? tw`bg-neutral_300 border-border_stroke cursor-default `
          : tw`bg-Primary border-Primary_600`,
        isFloatMode &&
          tw`max-md:(h-12 w-12 rounded-full fixed bottom-3 right-3 z-50 shadow-lg flex justify-center items-center border-0 p-0)`,
        customStyle,
      ]}
      disabled={disable}
      {...rest}
    >
      {isSubmitting ? (
        <div tw="flex justify-center items-center gap-2">
          <div css={disable && tw`opacity-50`}>{text}</div>
          <LoadingElement width={20} height={20} />
        </div>
      ) : (
        <div css={[disable && tw`opacity-50 `, textElementStyle]}>
          {isFloatMode ? <img loading="lazy" decoding="async" src={plus} alt={'plus icon'} width={24} height={24} /> : text}
        </div>
      )}
    </button>
  );
};

export default PrimaryButton;
